import { useEffect, useState } from 'react';

import { useAddToast } from '../../../../contexts/ToastContext';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import useAsyncValue from '../../../../hooks/useAsyncValue';
import { patch } from '../../../../redux/api';
import type { Category } from '../../../../../shared/issuesV2';
import type { FailedFetchResponse } from '../../../../helpers/errors';

import { convertToFieldsToPropagate, getEndpoint } from './utils';
import type { GenericIssueSettingsFields, PropagateIssueCheckboxes } from './utils';

const toggleValue = (value: unknown) => !value;

type UsePropagateIssueCheckboxes = PropagateIssueCheckboxes & {
  toggleIsScanningEnabled: () => void;
  toggleShouldUseSelectedPolicy: () => void;
  toggleIsCICheckEnabled: () => void;
  toggleShouldUseSelectedFilter: () => void;
  isSaveButtonDisabled: boolean;
};

/**
 * Manages the state of the checkboxes for the issue settings propagate modal.
 * Note that this does not handle the container settings propagate modal,
 * as that is slightly different and is handled separately.
 */
function usePropagateSettingsModalCheckboxes(isOpen: boolean): UsePropagateIssueCheckboxes {
  const [isScanningEnabled, setIsScanningEnabled] = useState<boolean>(false);
  const [shouldUseSelectedPolicy, setShouldUseSelectedPolicy] = useState<boolean>(false);
  const [isCICheckEnabled, setIsCICheckEnabled] = useState<boolean>(false);
  const [shouldUseSelectedFilter, setShouldUseSelectedFilter] = useState<boolean>(false);

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setIsScanningEnabled(false);
    setShouldUseSelectedPolicy(false);
    setIsCICheckEnabled(false);
    setShouldUseSelectedFilter(false);
  }, [isOpen, setIsScanningEnabled, setShouldUseSelectedPolicy, setIsCICheckEnabled, setShouldUseSelectedFilter]);

  return {
    isScanningEnabled,
    toggleIsScanningEnabled: () => setIsScanningEnabled(toggleValue),
    shouldUseSelectedPolicy,
    toggleShouldUseSelectedPolicy: () => setShouldUseSelectedPolicy(toggleValue),
    isCICheckEnabled,
    toggleIsCICheckEnabled: () => setIsCICheckEnabled(toggleValue),
    shouldUseSelectedFilter,
    toggleShouldUseSelectedFilter: () => setShouldUseSelectedFilter(toggleValue),
    isSaveButtonDisabled: ![
      isScanningEnabled,
      shouldUseSelectedPolicy,
      isCICheckEnabled,
      shouldUseSelectedFilter,
    ].some(value => value),
  };
}

/**
 * Manages the PATCH request to propagate the licensing settings
 */
function usePropagateSettingsModalSave({
  orgId,
  category,
  fieldsToPropagate,
  onHide,
}: {
  orgId: number;
  category: Category;
  fieldsToPropagate: GenericIssueSettingsFields[];
  onHide: () => void;
}) {
  const addToast = useAddToast();
  const asyncValue = useAsyncValue<void>();

  async function onSavePropagateSettings(additionalFields: GenericIssueSettingsFields[] = []) {
    try {
      asyncValue.setPending();
      await patch<GenericIssueSettingsFields[]>(getEndpoint(orgId, category), [
        ...fieldsToPropagate,
        ...additionalFields,
      ]);
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(
        errorMessagesListFromResponse(error as FailedFetchResponse),
      );
    }
  }

  return {
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    onSavePropagateSettings,
  };
}

/**
 * Hook which combines each of the above hooks.
 */
function usePropagateSettingsModal({
  orgId,
  category,
  isOpen,
  onHide,
}: {
  orgId: number;
  category: Category;
  isOpen: boolean;
  onHide: () => void;
}) {
  const checkboxes = usePropagateSettingsModalCheckboxes(isOpen);
  const { onSavePropagateSettings, isLoading, errors } = usePropagateSettingsModalSave({
    orgId,
    category,
    onHide,
    fieldsToPropagate: convertToFieldsToPropagate(category, checkboxes),
  });

  return {
    ...checkboxes,
    isLoading,
    errors,
    onSavePropagateSettings,
  };
}

export default usePropagateSettingsModal;
